/**
 * Confirmation feedback for a successful scan.
 *
 * Worth doing properly: the operator is looking at the label, not the screen.
 * A visual-only confirmation means holding the phone up to check whether the
 * scan landed, which is most of the time cost of scanning something.
 */

let audioContext: AudioContext | null = null

/** Short, dry confirmation tone. Synthesised so there is no audio file to fetch. */
export function beep(): void {
  try {
    // Created lazily and reused: constructing an AudioContext per scan leaks
    // hardware contexts, and browsers cap how many can exist.
    audioContext ??= new AudioContext()
    const ctx = audioContext

    // iOS suspends the context whenever the app is backgrounded.
    if (ctx.state === 'suspended') void ctx.resume()

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.value = 1180
    // Ramp rather than switch: an instantaneous gain change produces an audible
    // click that sounds like a fault.
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12)

    osc.connect(gain).connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.13)
    osc.onended = () => {
      osc.disconnect()
      gain.disconnect()
    }
  } catch {
    /* audio is a nicety; never let it break a scan */
  }
}

/**
 * Haptic tick.
 *
 * Note for anyone testing on an iPhone and finding nothing happens: iOS Safari
 * does not implement the Vibration API at all. This fires on Android only, and
 * is why the beep exists as the primary confirmation rather than a fallback.
 */
export function vibrate(pattern: number | number[] = 30): void {
  try {
    navigator.vibrate?.(pattern)
  } catch {
    /* ignore */
  }
}

export function confirmScan(sound: boolean): void {
  vibrate(30)
  if (sound) beep()
}

export function errorFeedback(): void {
  vibrate([40, 60, 40])
}
